import { useMemo } from 'react'
import Image from 'next/image'
import clsx from 'clsx'

/* blocks */
import Link from '@/atoms/Link/Link'
import CarouselAlt from '@/cmp/organism/Carousel/CarouselAlt'

/* utils */
import { dataLayerPostClickEvent } from '@/utils/functions/dataLayerEvents'
import { AllCaseStudyPageAttributes } from '@/utils/queries/customer_case_study'

/* assets */
import { ReactComponent as ChevronRightWhiteIcon } from 'public/assets/icons/chevron-right-white.svg'

interface Props {
  title: string
  data: {
    attributes: AllCaseStudyPageAttributes
  }[]
  mode?: 'dark' | 'light'
}

function MoreCaseStudies({ title, data, mode = 'dark' }: Props) {
  const slides = useMemo(() => {
    return data.map(list => <Slide item={list} key={list.attributes.slug} />)
  }, [data])

  return (
    <section
      className={clsx(
        mode === 'dark'
          ? 'bg-blue-web-90 text-white'
          : 'bg-white text-blue-web-90'
      )}
    >
      <div className="container | py-12 lg:py-16">
        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-left | max-w-col-8 mb-8 md:mb-12">
          {title}
        </h2>

        <CarouselAlt
          sliderClassName="mb-10"
          className="mb-10"
          autoScroll={false}
          navigationType="dot"
          slides={slides}
        />
      </div>
    </section>
  )
}

const Slide = ({
  item
}: {
  item: {
    attributes: AllCaseStudyPageAttributes
  }
}) => {
  return (
    <Link
      key={item.attributes.slug}
      href={`/case-study/${item.attributes.slug}/`}
      onClick={() =>
        dataLayerPostClickEvent({
          title: item.attributes.heroTitle,
          type: 'case-study',
          url: `/case-study/${item.attributes.slug}`
        })
      }
      className={clsx(
        'font-heading | flex flex-col | max-w-[368px] rounded-2xl'
      )}
    >
      {item.attributes.customerLogo ? (
        <div className="relative">
          {item.attributes.caseStudyCardBgImage ? (
            <Image
              src={item.attributes.caseStudyCardBgImage.data.attributes.url}
              alt={
                item.attributes.caseStudyCardBgImage.data.attributes
                  .alternativeText ?? ''
              }
              height={
                item.attributes.caseStudyCardBgImage.data.attributes.height
              }
              width={item.attributes.caseStudyCardBgImage.data.attributes.width}
              className="object-cover rounded-2xl"
            />
          ) : null}
          <div className="text-white | absolute top-0 | h-full p-5 md:p-7">
            <div className="h-22 md:h-24 lg:h-36">
              <Image
                src={item.attributes.customerLogoWhite.data.attributes.url}
                alt={
                  item.attributes.customerLogoWhite.data.attributes
                    .alternativeText ?? ''
                }
                height={
                  item.attributes.customerLogoWhite.data.attributes.height
                }
                width={item.attributes.customerLogoWhite.data.attributes.width}
                className="w-28 md:w-32"
              />
            </div>
            <h2 className="text-xl font-semibold | line-clamp-4 h-24 mb-5 md:mb-8 lg:mb-4">
              {item.attributes.heroTitle}
            </h2>
            <hr className="text-white | mb-4 lg:mb-2" />
            <div className="flex gap-4 md:gap-6 | mb-9 md:mb-7 h-20">
              {item.attributes.statisticsList.slice(0, 2).map(stat => (
                <div key={stat.id}>
                  <div
                    className="text-3xl | md:mb-2"
                    dangerouslySetInnerHTML={{ __html: stat.title ?? '' }}
                  />
                  <div
                    className="font-body text-caption h-7"
                    dangerouslySetInnerHTML={{ __html: stat.description ?? '' }}
                  />
                </div>
              ))}
            </div>
            <div className="text-base font-semibold text-blue-web-90  |  flex items-center gap-2">
              Read Case Study
              <div className="bg-blue-web-90 p-2 rounded-full">
                <ChevronRightWhiteIcon />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </Link>
  )
}

export default MoreCaseStudies
